<script setup>
import { useAccreditation } from '@/composables/useAccreditation'

definePage({
  meta: {
    layout: 'default',
    action: 'standard-manage',
    subject: 'Accreditation',
  },
})

const {
  lpas, jenisFasyankesList, tree, loading, saving,
  fetchLpas, fetchJenisFasyankes, fetchTree, createNode, updateNode, deleteNode,
} = useAccreditation()

const lpaId = ref(null)
const jenisId = ref(null)

onMounted(async () => {
  await Promise.all([fetchLpas(), fetchJenisFasyankes()])
})

const hasSelection = computed(() => !!lpaId.value && !!jenisId.value)

const load = () => { if (hasSelection.value) fetchTree(lpaId.value, jenisId.value) }

watch([lpaId, jenisId], load)

const lpaOptions = computed(() => lpas.value.map(l => ({ title: l.name, value: l.id })))
const jenisOptions = computed(() => jenisFasyankesList.value.map(j => ({ title: j.name, value: j.id })))

const dialog = ref(false)
const editingId = ref(null)
const form = ref({ kode: '', judul: '', tipe: 'struktur', skor_maksimal: 100, parent_id: null })
const errors = ref({})

function openAddRoot() {
  editingId.value = null
  form.value = { kode: '', judul: '', tipe: 'struktur', skor_maksimal: 100, parent_id: null }
  errors.value = {}
  dialog.value = true
}

function openAddChild(parent) {
  editingId.value = null
  form.value = { kode: '', judul: '', tipe: 'ep', skor_maksimal: 100, parent_id: parent.id }
  errors.value = {}
  dialog.value = true
}

function openEdit(node) {
  editingId.value = node.id
  form.value = { kode: node.kode, judul: node.judul, tipe: node.tipe, skor_maksimal: node.skor_maksimal ?? 100, parent_id: node.parent_id }
  errors.value = {}
  dialog.value = true
}

async function save() {
  errors.value = {}
  try {
    if (editingId.value) {
      await updateNode(editingId.value, form.value)
    } else {
      await createNode({ ...form.value, lpa_id: lpaId.value, jenis_fasyankes_id: jenisId.value })
    }
    dialog.value = false
    load()
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save accreditation standard error:', e)
  }
}

const deleteDialog = ref(false)
const deletingNode = ref(null)

function confirmDelete(node) {
  deletingNode.value = node
  deleteDialog.value = true
}

async function doDelete() {
  try {
    await deleteNode(deletingNode.value.id)
    deleteDialog.value = false
    load()
  } catch (e) {
    console.error('Delete accreditation standard error:', e)
  }
}
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold">
          Standar Akreditasi
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Pohon Bab &rarr; Standar &rarr; Elemen Penilaian (§9.8)
        </p>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol
            cols="12"
            md="5"
          >
            <VSelect
              v-model="lpaId"
              :items="lpaOptions"
              label="LPA"
              density="compact"
            />
          </VCol>
          <VCol
            cols="12"
            md="5"
          >
            <VSelect
              v-model="jenisId"
              :items="jenisOptions"
              label="Jenis Fasyankes"
              density="compact"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
            class="d-flex align-center"
          >
            <VBtn
              block
              :disabled="!hasSelection"
              prepend-icon="ri-add-line"
              @click="openAddRoot"
            >
              Tambah Bab
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VCard v-if="hasSelection">
      <VCardText>
        <VProgressLinear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-3"
        />
        <StandardTree
          :nodes="tree"
          editable
          @add-child="openAddChild"
          @edit="openEdit"
          @delete="confirmDelete"
        />
        <p
          v-if="!loading && !tree.length"
          class="text-medium-emphasis text-center py-6"
        >
          Belum ada node untuk kombinasi LPA + Jenis Fasyankes ini.
        </p>
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ editingId ? 'Edit Node' : 'Tambah Node' }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol
              v-if="!editingId"
              cols="12"
            >
              <VSelect
                v-model="form.tipe"
                :items="[{ title: 'Struktur (Bab/Standar)', value: 'struktur' }, { title: 'Elemen Penilaian (EP)', value: 'ep' }]"
                label="Tipe *"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.kode"
                label="Kode *"
                :error-messages="errors.kode"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.judul"
                label="Judul *"
                :error-messages="errors.judul"
              />
            </VCol>
            <VCol
              v-if="form.tipe === 'ep'"
              cols="12"
            >
              <VTextField
                v-model.number="form.skor_maksimal"
                type="number"
                label="Skor Maksimal *"
                :error-messages="errors.skor_maksimal"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Node?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingNode?.judul }}</strong> akan dihapus. Node dengan sub-node tidak bisa dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
